import { cidForDirItemId, cidForDirValue } from '../../concept/cid'
import { ASTNode, NodeType, ExpressionNode, ForLoopNode, DynamicFunctionNode, getChildNodes } from './AstNode'
import { CalcScriptCompileEnv } from './CompileEnv'
import type { CalcScriptCompileError } from './CompilerTypes'
import { err, ok, Result } from 'neverthrow'

export interface CalcScriptConceptCollection {
	conceptIds: Set<string>
	subconceptIds: Set<string>
}

// identify the concepts referenced by a script
export class CalcScriptConceptCollector {
	private isUsed = false
	private collection: CalcScriptConceptCollection = {
		conceptIds: new Set(),
		subconceptIds: new Set(),
	}
	private currentLoopVariable: string = ""
	private currentLoopExpression: ExpressionNode | null = null

	constructor(private rootNode: ASTNode, private environment: CalcScriptCompileEnv) { }

	collect(): Result<CalcScriptConceptCollection, CalcScriptCompileError> {
		if(this.isUsed) {
			return err({
				area: 'CalcScriptConceptCollector',
				message: 'Attempted to reuse instance',
			})
		}
		this.isUsed = true
		const rTraverse = this.traverseNode(this.rootNode)
		if(rTraverse.isErr()) {
			return err(rTraverse.error)
		}
		return ok(this.collection)
	}

	private traverseNode(node: ASTNode): Result<void, CalcScriptCompileError> {
		switch (node.type) {
			case NodeType.Value:
				return this.handleValueExpression(node)
			case NodeType.AnySelected:
				return this.handleAnySelectedExpression(node)
			case NodeType.HasAnyValue:
			case NodeType.DirValidity:
			case NodeType.Titles:
			// SubconceptIds don't need to be added to the collector unless they call VALUE on it
			// case NodeType.SubconceptIds:
				return this.addFirstArgumentToConcepts(node)
			case NodeType.ForLoop:
				return this.handleForLoop(node)
			default:
				getChildNodes(node).forEach(child => this.traverseNode(child))
				return ok()
		}
	}

	private handleValueExpression(node: DynamicFunctionNode): Result<void, CalcScriptCompileError> {
		const concept = node.arguments[0]
		const subconcept = node.arguments[1]
		if (concept) {
			const r = this.getValueExpressionConceptId(concept)
			if(r.isErr()) {
				return err(r.error)
			}
			this.collection.conceptIds.add(r.value)
		}
		if (subconcept) {
			const r = this.getValueExpressionConceptId(subconcept)
			if(r.isErr()) {
				return err(r.error)
			}
			this.collection.subconceptIds.add(r.value)
		}
		return ok()
	}

	// same inputs as VALUE(concept, subconcept) for each listed subconcept
	private handleAnySelectedExpression(node: DynamicFunctionNode): Result<void, CalcScriptCompileError> {
		const [concept, ...subconcepts] = node.arguments
		if (concept) {
			const r = this.getValueExpressionConceptId(concept)
			if(r.isErr()) {
				return err(r.error)
			}
			this.collection.conceptIds.add(r.value)
		}
		for (const subconcept of subconcepts) {
			const r = this.getValueExpressionConceptId(subconcept)
			if(r.isErr()) {
				return err(r.error)
			}
			this.collection.subconceptIds.add(r.value)
		}
		return ok()
	}

	private getValueExpressionConceptId(conceptNode: ExpressionNode): Result<string, CalcScriptCompileError> {
		let node: ExpressionNode = conceptNode
		if (this.isLoopVariable(conceptNode)) {
			if (!this.currentLoopExpression) {
				return err({
					area: 'CalcScriptConceptCollector',
					message: 'Loop expression not set',
					line: node.line,
					column: node.column,
				})
			}
			node = this.currentLoopExpression
		}
		const result = this.getConceptIdString(node)
		if (result.isErr()) {
			return err(result.error)
		}
		return ok(result.value)
	}

	private isLoopVariable(expression: ExpressionNode): boolean {
		return !!this.currentLoopVariable &&
			expression.type === NodeType.Identifier &&
			expression.name === this.currentLoopVariable
	}

	private addFirstArgumentToConcepts(node: DynamicFunctionNode): Result<void, CalcScriptCompileError> {
		const concept = node.arguments[0]

		if (concept) {
			const result = this.getConceptIdString(concept)
			if (result.isErr()) {
				return err(result.error)
			}
			this.collection.conceptIds.add(result.value)
		}

		return ok()
	}

	private handleForLoop(node: ForLoopNode): Result<void, CalcScriptCompileError> {
		if (node.collection.type !== NodeType.Array) {
			return ok()
		}

		const previousLoopVariable = this.currentLoopVariable
		const previousLoopExpression = this.currentLoopExpression

		this.currentLoopVariable = node.loopVariable

		for (let expression of node.collection.elements) {
    	this.currentLoopExpression = expression
			for (const stmt of node.loopBody) {
				const result = this.traverseNode(stmt)
				if (result.isErr()) {
					this.currentLoopVariable = previousLoopVariable
					this.currentLoopExpression = previousLoopExpression
					return err(result.error)
				}
			}
		}

		this.currentLoopVariable = previousLoopVariable
		this.currentLoopExpression = previousLoopExpression
		return ok()
	}

	private getConceptIdString(expression: ExpressionNode): Result<string, CalcScriptCompileError> {
		switch (expression.type) {
			case NodeType.StringLiteral:
				return ok(expression.value.replace(/^"|"$/g, ''))
			case NodeType.ConceptId:
				const argument = expression.arguments[0]
				if (argument.type === NodeType.StringLiteral) {
					return ok(argument.value.replace(/^"|"$/g, ''))
				}
				break
			case NodeType.DirId:
				const dirArg = expression.arguments[0]
				if (dirArg.type === NodeType.NumericLiteral) {
					return ok(cidForDirItemId(dirArg.value))
				}
				break
			case NodeType.DirValueId:
				const dirValueArg = expression.arguments[0]
				if (dirValueArg.type === NodeType.StringLiteral) {
					return ok(cidForDirValue(dirValueArg.value.replace(/^"|"$/g, '')))
				}
				break
			case NodeType.Identifier:
				const result = this.environment.getScopeExpression(expression.name)
				if (result.isErr()) {
					return err({
						area: 'CalcScriptConceptCollector',
						message: result.error,
						line: expression.line,
						column: expression.column,
					})
				}
				return this.getConceptIdString(result.value)
		}
		return err({
			area: 'CalcScriptConceptCollector',
			message: `Invalid Concept ID (must be string literal, ID function call with string literal arguments, or identifier referencing string value)`,
			line: expression.line,
			column: expression.column,
		})
	}
}
